/**
 * Modes Style04 - Smart Blind Open / Pause / Close action controls
 */
import { useEffect, useState } from 'react'
import type { TraitControlProps } from './types'
import './ModesStyle04.css'

type BlindAction = 'open' | 'pause' | 'close'

const ACTIONS: Array<{ id: BlindAction; label: string; icon: string }> = [
  { id: 'open', label: 'Open', icon: 'arrow_upward' },
  { id: 'pause', label: 'Pause', icon: 'pause' },
  { id: 'close', label: 'Close', icon: 'arrow_downward' },
]

function getInitialAction(state: Record<string, unknown>): BlindAction {
  const raw = state?.openClosePercent ?? state?.openPercent ?? state?.position
  const percent = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isFinite(percent)) return 'pause'
  if (percent <= 0) return 'close'
  if (percent >= 100) return 'open'
  return 'pause'
}

export function ModesStyle04({
  state,
  onCommand,
  disabled = false,
}: TraitControlProps) {
  const [active, setActive] = useState<BlindAction>(() => getInitialAction(state))

  useEffect(() => {
    setActive(getInitialAction(state))
  }, [state?.openClosePercent, state?.openPercent, state?.position])

  const runAction = (action: BlindAction) => {
    setActive(action)
    if (action === 'open') {
      void onCommand('setPosition', { openClosePercent: 100 })
      return
    }
    if (action === 'close') {
      void onCommand('setPosition', { openClosePercent: 0 })
      return
    }
    void onCommand('pause')
  }

  return (
    <div className="trait-modes-style04">
      <div className="trait-modes-style04__grid">
        {ACTIONS.map((action) => {
          const isActive = active === action.id
          const slotSuffix = action.id[0].toUpperCase() + action.id.slice(1)
          return (
            <button
              key={action.id}
              type="button"
              className={`trait-modes-style04__button${isActive ? ' active' : ''}`}
              onClick={() => runAction(action.id)}
              disabled={disabled}
              data-css-slot={`blindAction${slotSuffix}Button`}
              data-css-slot-label={`Blind Action ${slotSuffix} Button`}
              data-css-state={isActive ? 'on active' : 'off inactive'}
            >
              <span
                className="material-symbols-rounded trait-modes-style04__icon"
                data-css-slot={`blindAction${slotSuffix}Icon`}
                data-css-slot-label={`Blind Action ${slotSuffix} Icon`}
                data-css-state={isActive ? 'on active' : 'off inactive'}
              >
                {action.icon}
              </span>
              <span
                className="trait-modes-style04__label"
                data-css-slot={`blindAction${slotSuffix}Label`}
                data-css-slot-label={`Blind Action ${slotSuffix} Label`}
                data-css-state={isActive ? 'on active' : 'off inactive'}
              >
                {action.label}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
